import type { JsonApiErrorObject } from '../types/error'

import { ApiError } from '../types/error'

interface ErrorPayload {
  errors?: JsonApiErrorObject[]
  error?: JsonApiErrorObject | { errors?: JsonApiErrorObject[] }
  message?: string
  detail?: string
  title?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function asPayload(value: unknown): ErrorPayload | undefined {
  return isRecord(value) ? value as ErrorPayload : undefined
}

function firstJsonApiErrorFromPayload(payload: unknown): JsonApiErrorObject | undefined {
  const data = asPayload(payload)
  if (!data)
    return undefined

  if (Array.isArray(data.errors) && data.errors[0])
    return data.errors[0]

  const nestedError = asPayload(data.error)
  if (Array.isArray(nestedError?.errors) && nestedError.errors[0])
    return nestedError.errors[0]

  if (data.error && isRecord(data.error))
    return data.error as JsonApiErrorObject

  if (typeof data.detail === 'string' || typeof data.title === 'string') {
    return {
      detail: data.detail,
      title: data.title,
    }
  }

  return undefined
}

function firstJsonApiError(error: unknown): JsonApiErrorObject | undefined {
  if (!isRecord(error))
    return undefined

  return firstJsonApiErrorFromPayload(error.data)
    ?? firstJsonApiErrorFromPayload((error.response as Record<string, unknown> | undefined)?._data)
    ?? firstJsonApiErrorFromPayload(error.response)
    ?? firstJsonApiErrorFromPayload(error)
}

function formatSeconds(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

function friendlyErrorDetail(detail: string): string {
  const flameInputPrefix = 'invalid flame animation input:'
  if (!detail.startsWith(flameInputPrefix))
    return detail

  const reason = detail.slice(flameInputPrefix.length).trim()
  if (reason.includes('time range exceeds npz frame count')) {
    const duration = Number(reason.match(/\bduration=(\d+(?:\.\d+)?)s\b/)?.[1])
    if (Number.isFinite(duration)) {
      const seconds = formatSeconds(duration)
      return `呼吸态时间范围超过动画数据长度：当前动画数据约 ${seconds} 秒，请把 End Time 调整到 ${seconds} 秒以内。`
    }
    return '呼吸态时间范围超过动画数据长度，请缩短 End Time 后重试。'
  }

  return reason || detail
}

function jsonApiErrorToMessage(error: JsonApiErrorObject, defaultMessage?: string): string {
  const detail = error.detail ? friendlyErrorDetail(error.detail) : undefined
  const title = error.source?.pointer || error.source?.header || error.source?.parameter
  if (title && detail)
    return `${title} : ${detail}`
  return detail ?? error.title ?? defaultMessage ?? '未知错误'
}

export function errorToMessage(error: unknown, defaultMessage?: string): string {
  if (error instanceof ApiError) {
    return jsonApiErrorToMessage(error.error, defaultMessage)
  }

  const jsonApiError = firstJsonApiError(error)
  if (jsonApiError) {
    return jsonApiErrorToMessage(jsonApiError, defaultMessage)
  }
  else if (error instanceof Error) {
    return error.message
  }
  return String(error || defaultMessage || '未知错误')
}
